// ExcelImportDialog.js
import React, { useState, useRef, useCallback } from 'react';
import * as XLSX from 'xlsx';
import invoiceService from './invoice.service';
import dropdownService from '../software-dynamic-version-5/api.service-v-5';

const ExcelImportDialog = ({ visible, onClose, onDataLoaded }) => {
  const [file, setFile]                         = useState(null);
  const [isDragging, setIsDragging]             = useState(false);
  const [rawRows, setRawRows]                   = useState([]);
  const [verifiedRows, setVerifiedRows]         = useState([]);
  const [isVerifying, setIsVerifying]           = useState(false);
  const [verificationDone, setVerificationDone] = useState(false);
  const [hasAnyError, setHasAnyError]           = useState(false);
  const fileInputRef = useRef(null);

  const resetState = () => {
    setFile(null);
    setRawRows([]);
    setVerifiedRows([]);
    setIsVerifying(false);
    setVerificationDone(false);
    setHasAnyError(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const REQUIRED_COLUMNS = [
  'hS_CODE',
  'ProductDescription',
  'Quantity',
  'Rate',
  'UnitOfMeasurement',
  'TaxPercent',
  'FBRQuantity',
  'FBRUnitOfMeasurement'
];
  const readExcelFile = (uploadedFile) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data  = new Uint8Array(e.target.result);
        const wb    = XLSX.read(data, { type: 'array' });
        const ws    = wb.Sheets[wb.SheetNames[0]];
        const rows  = XLSX.utils.sheet_to_json(ws, { defval: '' });
        
        // const withId = rows.map((r, i) => ({ ...r, ExcelRowID: i + 1 }));
        const jsonData = XLSX.utils.sheet_to_json(ws, { defval: '' });

// 👉 Get columns from first row
const fileColumns = Object.keys(jsonData[0] || {});

// 👉 Normalize function (important)
const normalize = (s) => String(s).trim().toLowerCase();

// 👉 Find missing columns
const missingColumns = REQUIRED_COLUMNS.filter(req =>
  !fileColumns.some(col => normalize(col) === normalize(req))
);

if (missingColumns.length > 0) {
  alert(`Missing required columns:\n${missingColumns.join(', ')}`);
  resetState();
  return;
}

// ✅ If valid → continue
const withId = jsonData.map((r, i) => ({ ...r, ExcelRowID: i + 1 }));
setRawRows(withId);
setVerifiedRows([]);
setVerificationDone(false);
setHasAnyError(false);

      } catch (err) {
        console.error('Excel read error:', err);
        alert('Could not read the Excel file. Please check the format.');
        resetState();
      }
    };
    reader.readAsArrayBuffer(uploadedFile);
  };

  const handleFileSelect = (uploadedFile) => {
    if (!uploadedFile) return;
    setFile(uploadedFile);
    readExcelFile(uploadedFile);
  };

  const handleDragOver  = (e) => { e.preventDefault(); setIsDragging(true); };
  const handleDragLeave = ()  => setIsDragging(false);
  const handleDrop      = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
    const f = e.dataTransfer.files[0];
    if (f) handleFileSelect(f);
  }, []);

  const handleVerify = async () => {
    if (!rawRows.length) return;
    setIsVerifying(true);
    try {
      const [hsRes, uomRes, fbrUomRes] = await Promise.all([
        dropdownService.getAllForList('HSCodeDDSource'),
        dropdownService.getAllForList('UnitOfMeasurementDDSource'),
        dropdownService.getAllForList('UOMINVOICECHALLANINPUT')
        
      ]);

      const hsSource     = hsRes?.data?.recordset     || [];
      const uomSource    = uomRes?.data?.recordset    || [];
      // const productSource    = productRes?.data?.recordset    || [];
      // const GDSource    = GDRse?.data?.recordset    || [];
      const fbrUomSource = fbrUomRes?.data?.recordset || [];
      const normalize    = (s) => String(s ?? '').trim().toLowerCase();

      const verified = rawRows.map((row) => {
        const errors   = [];
        let   hasError = false;
        const obj      = { ExcelRowID: row.ExcelRowID };

        // hS_CODE
        const hsExcel = String(row.hS_CODE ?? '').trim();
        if (hsExcel) {
          const hsMatch = hsSource.find((s) => normalize(s.value) === normalize(hsExcel));
          if (hsMatch) {
            obj.hS_CODE = hsMatch.id; obj.DescriptionCode = hsMatch.id;
          } else {
            hasError = true;
            // errors.hS_CODE = `HS Code "${hsExcel}" not found in lookup`;
            errors.push({ field: "hS_CODE", message: `HS Code "${hsExcel}" not found` });

            obj.hS_CODE = hsExcel; obj.DescriptionCode = hsExcel;
          }
        } else {
          obj.hS_CODE = ''; obj.DescriptionCode = '';
        }
     
        
          // ProductName
//         const ProductNameExcel = String(row.ProductName ?? '').trim();
//         if (ProductNameExcel) {
//           const productMatch = productSource.find((s) => normalize(s.value) === normalize(ProductNameExcel));
//           if (productMatch) {
//             obj.ProductID = productMatch.id;
//             obj.ProductName   = productMatch.value;
//           } else {
//             hasError = true;
//             // errors.ProductName = `UOM "${ProductNameExcel}" not found in lookup`;
//             errors.push({
//   field: "ProductName",
//   message: `Product "${ProductNameExcel}" not found`
// });
//             obj.ProductID  = null;
//             obj.ProductName    = ProductNameExcel;
//           }
//         } else {
//           obj.ProductID = null; obj.ProductName = '';
//         }
//           // GDNo
//         const GDNoExcel = String(row.GDNo ?? '').trim();
//         if (GDNoExcel) {
//           const GDMatch = GDSource.find((s) => normalize(s.value) === normalize(GDNoExcel));
//           if (GDMatch) {
//             obj.GDID = GDMatch.id;
//             obj.GDNo   = GDMatch.value;
//           } else {
//             hasError = true;
//             // errors.GDNo = `UOM "${GDNoExcel}" not found in lookup`;
//             errors.push({ field: "GDNo", message: `GD "${GDNoExcel}" not found` });

//             obj.GDID  = null;
//             obj.GDNo    = GDNoExcel;
//           }
//         } else {
//           obj.GDID = null; obj.GDNo = '';
//         }


        obj.ProductDescription       = String(row.ProductDescription ?? '').trim() || '-';
        obj.Quantity                 = parseFloat(row.Quantity)   || 0;
        obj.Rate                     = parseFloat(row.Rate)        || 0;
        obj.TaxPercent               = parseFloat(row.TaxPercent)  || 0;

        // UnitOfMeasurement
        const uomExcel = String(row.UnitOfMeasurement ?? '').trim();
        if (uomExcel) {
          const uomMatch = uomSource.find((s) => normalize(s.value) === normalize(uomExcel));
          if (uomMatch) {
            obj.UnitOfMeasurementID = uomMatch.id;
            obj.UnitOfMeasurement   = uomMatch.value;
          } else {
            hasError = true;
            // errors.UnitOfMeasurement = `UOM "${uomExcel}" not found in lookup`;
            errors.push({ field: "UnitOfMeasurement", message: `UOM "${uomExcel}" not found` });

            obj.UnitOfMeasurementID  = null;
            obj.UnitOfMeasurement    = uomExcel;
          }
        } else {
          obj.UnitOfMeasurementID = null; obj.UnitOfMeasurement = '';
        }

        obj.BillAmount               = parseFloat((obj.Quantity * obj.Rate).toFixed(2));
        obj.TaxAmount                = parseFloat((obj.BillAmount * (obj.TaxPercent / 100)).toFixed(2));
        obj.SpecialExciseDutyPercent = 0;
        obj.SpecialExciseDutyAmount  = 0;
        obj.DiscountPercent          = 0;
        obj.DiscountAmount           = 0;
        obj.GDID           = 0;
        obj.GDNo           = 'NONE';
        obj.ProductID           = 1;
        obj.ProductName           = 'TEST PRODUCT';
        obj.TotalAmount              = parseFloat((obj.BillAmount + obj.TaxAmount).toFixed(2));

        // FBRQuantity
        const fbrQtyRaw = String(row.FBRQuantity ?? '').trim();
        obj.FBRQuantity = fbrQtyRaw !== '' ? (parseFloat(fbrQtyRaw) || 0) : obj.Quantity;

        // FBRUnitOfMeasurement
        const fbrUomExcel = String(row.FBRUnitOfMeasurement ?? '').trim();
        if (fbrUomExcel) {
          const fbrUomMatch = fbrUomSource.find((s) => normalize(s.value) === normalize(fbrUomExcel));
          if (fbrUomMatch) {
            obj.FBRUnitOfMeasurementID = fbrUomMatch.id;
            obj.FBRUnitOfMeasurement   = fbrUomMatch.value;
          } else {
            hasError = true;
            // errors.FBRUnitOfMeasurement = `FBR UOM "${fbrUomExcel}" not found in lookup`;
            errors.push({ field: "FBRUnitOfMeasurement", message: `FBR UOM "${fbrUomExcel}" not found` });
            obj.FBRUnitOfMeasurementID  = null;
            obj.FBRUnitOfMeasurement    = fbrUomExcel;
          }
        } else {
          obj.FBRUnitOfMeasurementID = null; obj.FBRUnitOfMeasurement = '';
        }

        return { ...obj, hasError, errors };
      });

      setVerifiedRows(verified);
      setHasAnyError(verified.some((r) => r.hasError));
      setVerificationDone(true);
    } catch (err) {
      console.error('Verification error:', err);
      alert('Verification failed. Please try again.');
    } finally {
      setIsVerifying(false);
    }
  };

  const handleLoadAndClose = () => {
    if (!hasAnyError && verificationDone) {
      const cleanRows = verifiedRows.map(({ hasError, errors, ...rest }) => rest);
      onDataLoaded(cleanRows);
    }
    resetState();
    onClose();
  };

  const handleCancel = () => { resetState(); onClose(); };

  if (!visible) return null;

  const allVerified = verificationDone && !hasAnyError;
  const errorRows   = verifiedRows.filter((r) => r.hasError);

  return (
    <div style={S.overlay}>
      <div style={S.modal}>

        {/* HEADER */}
        <div style={S.header}>
          <div style={S.headerLeft}>
            <div style={S.headerIconBox}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                <polyline points="14 2 14 8 20 8"/>
                <line x1="12" y1="18" x2="12" y2="12"/>
                <line x1="9" y1="15" x2="15" y2="15"/>
              </svg>
            </div>
            <span style={S.headerTitle}>Import Excel File</span>
          </div>
          <button style={S.headerClose} onClick={handleCancel} title="Close">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        <div style={S.body}>

          {/* STEP INDICATOR */}
          <div style={S.steps}>
            <StepDot n="1" label="Upload File"  done={!!file}              active={!file} />
            <div style={{ ...S.stepLine, background: file ? '#2563EB' : '#E2E8F0' }} />
            <StepDot n="2" label="Verify Data"  done={verificationDone}   active={!!file && !verificationDone} />
            <div style={{ ...S.stepLine, background: verificationDone ? '#2563EB' : '#E2E8F0' }} />
            <StepDot n="3" label="Load to Grid" done={false}              active={allVerified} />
          </div>

          {/* DROP ZONE */}
          <div
            style={{ ...S.dropZone, ...(isDragging ? S.dropZoneActive : {}), ...(file ? S.dropZoneFilled : {}) }}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => !file && fileInputRef.current?.click()}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls"
              style={{ display: 'none' }}
              onChange={(e) => handleFileSelect(e.target.files[0])}
            />

            {file ? (
              <div style={S.fileRow}>
                <div style={S.fileIconBox}>
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#16A34A" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                    <polyline points="14 2 14 8 20 8"/>
                    <line x1="8" y1="13" x2="16" y2="13"/><line x1="8" y1="17" x2="16" y2="17"/>
                    <polyline points="10 9 9 9 8 9"/>
                  </svg>
                </div>
                <div style={S.fileInfo}>
                  <span style={S.fileName}>{file.name}</span>
                  <span style={S.fileRows}>{rawRows.length} rows detected</span>
                </div>
                <button
                  style={S.clearBtn}
                  onClick={(e) => { e.stopPropagation(); resetState(); }}
                  title="Remove file"
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                    <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                  </svg>
                </button>
              </div>
            ) : (
              <div style={S.dropPrompt}>
                <div style={S.dropIconCircle}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#94A3B8" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="16 16 12 12 8 16"/>
                    <line x1="12" y1="12" x2="12" y2="21"/>
                    <path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3"/>
                  </svg>
                </div>
                <p style={S.dropTitle}>Drop your Excel file here</p>
                <p style={S.dropSub}>or <span style={S.dropLink}>browse files</span> — .xlsx / .xls accepted</p>
              </div>
            )}
          </div>

          {/* REQUIRED COLUMNS */}
          {!file && (
            <div style={S.colHintBox}>
              <span style={S.colHintLabel}>Required columns</span>
              <div style={S.colTagRow}>
                {['hS_CODE','ProductDescription','Quantity','Rate','UnitOfMeasurement',
                  'TaxPercent','FBRQuantity','FBRUnitOfMeasurement'].map((col) => (
                  <span key={col} style={S.colTag}>{col}</span>
                ))}
              </div>
            </div>
          )}

          {/* VERIFY BUTTON */}
          {rawRows.length > 0 && !verificationDone && (
            <button
              style={{ ...S.verifyBtn, ...(isVerifying ? S.verifyBtnBusy : {}) }}
              onClick={handleVerify}
              disabled={isVerifying}
            >
              {isVerifying ? (
                <><span style={S.spinner} /> Verifying {rawRows.length} rows…</>
              ) : (
                <>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                  </svg>
                  Verify Data
                </>
              )}
            </button>
          )}

          {/* 
                ERROR PANEL 
          */}
          {verificationDone && hasAnyError && (
            <div style={S.errorPanel}>
              <div style={S.errorPanelHead}>
                <div style={S.errorPanelHeadLeft}>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#B91C1C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
                  </svg>
                  <span style={S.errorPanelTitle}>
                    {errorRows.length} row{errorRows.length !== 1 ? 's' : ''} failed verification
                  </span>
                </div>
                <button style={S.reuploadBtn} onClick={resetState}>Re-upload</button>
              </div>
              <div style={S.errorBody}>
                {errorRows.map((r) => (
                  <div key={r.ExcelRowID} style={S.errorRow}>
                    <span style={S.errorBadge}>Row {r.ExcelRowID + 1}</span>
                    <div style={S.errorTags}>
                      {/* {Object.entries(r.errors).map(([field, msg]) => (
                        <span key={field} style={S.errorTag} title={msg}>
                          {field}: {msg}
                        </span>
                      ))} */}
                     { r.errors.map((err, i) => (
  <span key={i} style={S.errorTag} title={err.message}>
    {err.field}: {err.message}
  </span>
))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* SUCCESS PANEL */}
          {allVerified && (
            <div style={S.successPanel}>
              <div style={S.successLeft}>
                <div style={S.successCheck}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#16A34A" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>
                </div>
                <div>
                  <p style={S.successTitle}>All {verifiedRows.length} rows verified</p>
                  <p style={S.successSub}>Ready to load into the detail grid</p>
                </div>
              </div>
              <button style={S.loadBtn} onClick={handleLoadAndClose}>
                Load &amp; Close
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/>
                </svg>
              </button>
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

/* ── Step Dot helper ── */
const StepDot = ({ n, label, done, active }) => (
  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 5 }}>
    <div style={{
      width: 28, height: 28, borderRadius: '50%',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: 11, fontWeight: 700,textTransform:'uppercase',
      background: done ? '#2563EB' : active ? '#EFF6FF' : '#F1F5F9',
      color:      done ? '#fff'    : active ? '#2563EB' : '#94A3B8',
      border:     done ? 'none'    : active ? '1.5px solid #2563EB' : '1.5px solid #E2E8F0',
      transition: 'all 0.25s'
    }}>
      {done
        ? <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
        : n
      }
    </div>
    <span style={{textTransform:'uppercase', fontSize: 10, color: active || done ? '#1E293B' : '#94A3B8', fontWeight: active || done ? 600 : 400, whiteSpace: 'nowrap' }}>
      {label}
    </span>
  </div>
);

/* ── STYLES ── */
const S = {
  overlay: {
    position: 'fixed', inset: 0,
    background: 'rgba(0,0,0,0.45)',
    zIndex: 99999,
    display: 'flex', alignItems: 'center', justifyContent: 'center'
  },
  modal: {
    background: '#fff',
    borderRadius: 10,
    width: 620,
    maxWidth: '96vw',
    minHeight: '90vh',
    overflowY: 'auto',
    boxShadow: '0 8px 40px rgba(0,0,0,0.18)',
    display: 'flex',
    flexDirection: 'column',
    fontFamily: "'Inter', 'Segoe UI', sans-serif"
  },

  /* header */
  header: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '14px 20px',
    borderBottom: '1px solid #E2E8F0',
    flexShrink: 0
  },
  headerLeft: { display: 'flex', alignItems: 'center', gap: 10 },
  headerIconBox: {
    width: 30, height: 30, borderRadius: 6,
    background: '#114bc7',
    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
  },
  headerTitle: { color: '#0F172A', fontWeight: 700, fontSize: 14, letterSpacing: 0.1 ,textTransform:'uppercase'},
  headerClose: {
    background: 'transparent', border: '1px solid #E2E8F0',
    color: '#64748B', cursor: 'pointer',
    borderRadius: 6, padding: '6px 8px',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    transition: 'background 0.15s'
  },

  body: { padding: '18px 20px 20px', display: 'flex', flexDirection: 'column', gap: 14 },

  /* steps */
  steps: {
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0,
    padding: '4px 0 8px'
  },
  stepLine: { height: 1.5, width: 60, transition: 'background 0.3s', margin: '0 6px', marginBottom: 18 },

  /* drop zone */
  dropZone: {
    border: '1.5px dashed #CBD5E1',
    borderRadius: 8,
    minHeight: 120,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    cursor: 'pointer',
    transition: 'all 0.2s',
    background: '#FAFBFC'
  },
  dropZoneActive: {
    borderColor: '#2563EB',
    background: '#F0F7FF',
  },
  dropZoneFilled: {
    cursor: 'default',
    borderStyle: 'solid',
    borderColor: '#D1FAE5',
    background: '#F0FDF4'
  },

  /* drop prompt */
  dropPrompt: { textAlign: 'center', padding: '16px 20px', userSelect: 'none' },
  dropIconCircle: {
    width: 46, height: 46, background: '#F1F5F9',
    borderRadius: '50%', display: 'flex', alignItems: 'center',
    justifyContent: 'center', margin: '0 auto 10px'
  },
  dropTitle: { margin: '0 0 5px', fontWeight: 600, color: '#334155', fontSize: 13,textTransform:'uppercase' },
  dropSub: { margin: 0, color: '#94A3B8', fontSize: 12,textTransform:'uppercase' },
  dropLink: { color: '#2563EB', fontWeight: 600 },

  /* file row */
  fileRow: {
    display: 'flex', alignItems: 'center', gap: 12,
    padding: '12px 16px', width: '100%', boxSizing: 'border-box'
  },
  fileIconBox: {
    width: 40, height: 40, borderRadius: 8,
    background: '#F0FDF4', border: '1px solid #D1FAE5',
    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
  },
  fileInfo: { flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', gap: 2 },
  fileName: { fontWeight: 600, color: '#0F172A', fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  fileRows: { color: '#64748B', fontSize: 11,textTransform:'uppercase' },
  clearBtn: {
    flexShrink: 0,
    background: '#F8FAFC', border: '1px solid #E2E8F0',
    color: '#64748B', cursor: 'pointer',
    borderRadius: 6, padding: '6px 8px',
    display: 'flex', alignItems: 'center', justifyContent: 'center'
  },

  /* column hint */
  colHintBox: {
    background: '#F8FAFC', border: '1px solid #E2E8F0',
    borderRadius: 8, padding: '10px 14px',
    display: 'flex', flexDirection: 'column', gap: 8
  },
  colHintLabel: { fontSize: 11,textTransform:'uppercase', fontWeight: 600, color: '#475569', textTransform: 'uppercase', letterSpacing: 0.5 },
  colTagRow: { display: 'flex', flexWrap: 'wrap', gap: 5 },
  colTag: {
    background: '#fff', border: '1px solid #E2E8F0',
    color: '#334155',textTransform:'uppercase', fontSize: 10, fontWeight: 500,
    padding: '2px 8px', borderRadius: 4, fontFamily: 'monospace'
  },

  /* verify button */
  verifyBtn: {
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7,
    padding: '11px', width: '100%',
    background: '#2563EB',
    color: '#fff', border: 'none', borderRadius: 7,
    fontWeight: 600,textTransform:'uppercase', fontSize: 13, cursor: 'pointer', letterSpacing: 0.2,
    transition: 'opacity 0.2s, background 0.2s'
  },
  verifyBtnBusy: { opacity: 0.65, cursor: 'not-allowed' },
  spinner: {
    display: 'inline-block', width: 13, height: 13,
    border: '2px solid rgba(255,255,255,0.35)',
    borderTopColor: '#fff', borderRadius: '50%',
    animation: 'spin 0.75s linear infinite'
  },

  /* error panel */
  errorPanel: {
    border: '1px solid #FECACA', borderRadius: 8, overflow: 'hidden'
  },
  errorPanelHead: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '9px 14px',
    background: '#FEF2F2',
    borderBottom: '1px solid #FECACA'
  },
  errorPanelHeadLeft: { display: 'flex', alignItems: 'center', gap: 7 },
  errorPanelTitle: { color: '#B91C1C', fontWeight: 600, fontSize: 12,textTransform:'uppercase' },
  reuploadBtn: {
    background: '#fff', border: '1px solid #FECACA',
    color: '#B91C1C', cursor: 'pointer',
    borderRadius: 5, padding: '4px 10px', fontSize: 11, fontWeight: 600,textTransform:'uppercase'
  },
  errorBody: {
    maxHeight: 220, overflowY: 'auto',
    padding: '6px 0', background: '#fff'
  },
  errorRow: {
    display: 'flex', alignItems: 'flex-start', gap: 10,
    padding: '7px 14px', borderBottom: '1px solid #FEF2F2'
  },
  errorBadge: {
    background: '#FEF2F2', border: '1px solid #FECACA',
    color: '#B91C1C', borderRadius: 4,
    padding: '1px 8px',textTransform:'uppercase', fontSize: 10, fontWeight: 700,
    whiteSpace: 'nowrap', marginTop: 2, flexShrink: 0
  },
  errorTags: { display: 'flex', flexWrap: 'wrap', gap: 5, flex: 1 },
  errorTag: {
    background: '#FFF7ED', border: '1px solid #FED7AA',
    borderRadius: 4, padding: '2px 8px',
    fontSize: 10, color: '#4e1806', cursor: 'help',textTransform:'uppercase' ,fontWeight:'bold'
  },

  /* success panel */
  successPanel: {
    background: '#F0FDF4', border: '1px solid #D1FAE5',
    borderRadius: 8, padding: '14px 16px',
    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12
  },
  successLeft: { display: 'flex', alignItems: 'center', gap: 12 },
  successCheck: {
    width: 36, height: 36, borderRadius: '50%',
    background: '#DCFCE7', border: '1px solid #BBF7D0',
    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
  },
  successTitle: { margin: '0 0 2px', fontWeight: 700, color: '#14532D', fontSize: 13,textTransform:'uppercase' },
  successSub: { margin: 0, color: '#166534', fontSize: 11 },
  loadBtn: {
    display: 'flex', alignItems: 'center', gap: 7,
    padding: '9px 18px', flexShrink: 0,
    background: '#16A34A',
    color: '#fff', border: 'none', borderRadius: 7,
    fontWeight: 600, fontSize: 12, cursor: 'pointer', letterSpacing: 0.2,textTransform:'uppercase',
    whiteSpace: 'nowrap'
  }
};

if (typeof document !== 'undefined' && !document.getElementById('exc-spin-style')) {
  const s = document.createElement('style');
  s.id = 'exc-spin-style';
  s.textContent = '@keyframes spin { to { transform: rotate(360deg); } }';
  document.head.appendChild(s);
}

export default ExcelImportDialog;